<template lang="pug">
AppDialog(
  :show="show"
  append-to-body
  size="medium"
  :title="title"
  @close="closeDialog"
)
  // eslint-disable vue/no-mutating-props
  .col-sm-12.container
    ValidationObserver(ref="dialogObserver")
      .row
        .col-xs-12
          ValidationProvider(
            v-slot="{ errors }"
            rules="required|min:3|max:50"
            name="name"
          )
            div(:class="{ 'has-error': errors[0] }")
              label.control-label.required(for="name") Policy name
                abbr(title="required") *
              AppInput#name(
                v-model="workBreakPolicy.name"
                type="text"
                placeholder="Enter a name for the policy (ex: Rest or Lunch)"
              )
              span.help-block {{ errors[0] }}
      .row
        .col-xs-12
          ValidationProvider(
            v-slot="{ errors }"
            name="members"
          )
            div(:class="{ 'has-error': errors[0] }")
              WorkBreakPoliciesMemberSelect(:work-break-policy="workBreakPolicy")
              span.help-block {{ errors[0] }}

      .row.form-group.boolean.optional
        .col-xs-12.checkbox
          AppCheckbox.form-group.boolean.optional.auto-add-new-members(v-model="workBreakPolicy.auto_add_new_users")
            span Automatically add new members to this policy

      .row
        .col-xs-6
          label.control-label Type
          AppSelect(
            v-model="selected"
            label="Type"
            :value="workBreakPolicy.paid"
            :options="paidOptions()"
            :select2-options="select2Options"
            :disabled="editing"
            :disabled-tooltip="noEditTooltip"
          )

        .col-xs-6
          ValidationProvider(
            v-slot="{ errors }"
            rules="required|numeric|min_value:300|max_value:14400"
            name="duration"
          )
            div(:class="{ 'has-error': errors[0] }")
              label.control-label.required(for="duration") Allotted duration
                abbr(title="required") *
              WorkBreakPolicyDurationInput#duration(v-model="workBreakPolicy.duration")
              span.help-block {{ errors[0] }}
      .row.form-group
        .col-xs-12
          label.control-label Restrictions
          AppSelect(
            v-model.number="restriction"
            label="Restrictions"
            :options="WORK_BREAK_RULES"
            :select2-options="select2Options"
            :disabled="editing"
            :disabled-tooltip="noEditTooltip"
          )
        .col-xs-12
          span.inline-control-label {{ noEditTooltip }}
      .row.form-group(v-if="hasRestriction")
        .col-xs-12
          .inline-form-row.form-group(v-if="hasRepeatedRestriction")
            .col.pt-5
              span.inline-control-label.py-5.pl-0 Repeats every
            ValidationProvider(
              v-slot="{ errors }"
              :rules="`required|numeric|min_block:1800|max_block:${maxBlockDuration}`"
              name="repeat"
            )
              .col-xs-4.p-0(:class="{ 'has-error': errors[0] }")
                .input-group.duration-input
                  WorkBreakPolicyDurationInput#repeat(
                    v-model="blockDuration"
                    placeholder="4"
                    :disabled="editing"
                    :disabled-tooltip="noEditTooltip"
                  )
                  span.input-group-addon hrs
                span.help-block {{ errors[0] }}
          .inline-form-row.form-group
            .col.pt-5
              AppSwitch.py-5.pl-0(
                v-model="minimum"
                :label="ruleLabel"
                :disabled="editing"
              )
            ValidationProvider(
              v-slot="{ errors }"
              :rules="{ required: requireMinimum, numeric: true, min_rule: 300, max_rule: 43200 }"
              name="min"
              skip-if-empty
            )
              .col-xs-5.mr-5.p-0(:class="{ 'has-error': errors[0] }")
                .input-group.duration-input
                  WorkBreakPolicyDurationInput(
                    v-model="workBreakPolicy.earn_req_per_session"
                    :placeholder="rulePlaceholder"
                    :disabled="!minimum || editing"
                    :disabled-tooltip="noEditTooltip"
                  )
                  span.input-group-addon hrs
                span.help-block {{ errors[0] }}
              .col-xs-3.duration-postfix.pl-0.pt-5(v-if="hasWithinRestriction")
                span.inline-control-label.mt-5 first
          .form-group
            WorkBreakPolicyTimelineGraph(
              :rule="restriction"
              :minimum="minimum"
            )
      .row.form-group.boolean.optional
        .col-xs-12.checkbox
          AppCheckbox.form-group.boolean.optional.auto-add-new-members(v-model="workBreakPolicy.notify_members")
            span.notify_members Notify members about their breaks
            AppTooltipIcon(
              placement="top"
              message="Notifications are sent via our Mobile and Desktop apps"
              :size="12"
            )
  template(slot="footer")
    AppButton.cancel(@click="closeDialog") Cancel
    AppButton(
      type="primary"
      :disabled="sendingRequest"
      @click="save"
    ) Save
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import { mapGetters } from 'vuex';
import { extend } from 'vee-validate';

import { min, max, max_value as maxValue, min_value as minValue } from 'vee-validate/dist/rules';
import { WORK_BREAK_RULES } from '~/app_constants';
import WorkBreakPoliciesMemberSelect from './WorkBreakPoliciesMemberSelect.vue';
import WorkBreakPolicyDurationInput from './WorkBreakPolicyDurationInput.vue';
import WorkBreakPolicyTimelineGraph from './WorkBreakPolicyTimelineGraph.vue';
import AppSwitch from '../AppSwitch.vue';

extend('min', {
  ...min,
  message: `is too short`,
});

extend('max', {
  ...max,
  message: `is too long`,
});

extend('max_value', {
  ...maxValue,
  message: `must be less than or equal to 4 hours`,
});

extend('min_value', {
  ...minValue,
  message: `must be greater than or equal to 5 minutes`,
});

extend('min_rule', {
  ...minValue,
  message: `must be greater than or equal to 5 minutes`,
});

extend('max_rule', {
  ...maxValue,
  message: `must be less than or equal to 12 hours`,
});

extend('min_block', {
  ...minValue,
  message: `must be greater than or equal to 30 minutes`,
});

extend('max_block', {
  ...maxValue,
  message: (_, { max }) => `must be less than or equal to ${max / 3600} hours`,
});

extend('max_earn', {
  ...maxValue,
  message: `must be less than or equal to 12 hours`,
});

export default {
  name: 'WorkBreakPolicyDialog',
  components: {
    WorkBreakPoliciesMemberSelect,
    WorkBreakPolicyDurationInput,
    WorkBreakPolicyTimelineGraph,
    AppSwitch,
  },
  props: {
    show: Boolean,
    workBreakPolicy: {
      type: Object,
      default: () => ({
        name: '',
        auto_add_new_users: true,
        paid: true,
        duration: null,
        members: [],
        restriction_type: 0,
        block_duration: 0,
        earn_req_per_session: 0,
        notify_members: true,
      }),
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      select2Options: {
        allowClear: false,
        multiple: false,
        width: '70%',
      },
      sendingRequest: false,
      WORK_BREAK_RULES,
    };
  },
  computed: {
    ...mapGetters(['availableMembers', 'workBreaksSettings']),
    selected: {
      get() {
        return this.workBreakPolicy.paid ? 1 : 0;
      },
      set(value) {
        this.workBreakPolicy.paid = value === 1;
      },
    },
    restriction: {
      get() {
        return this.workBreakPolicy.restriction_type || 0;
      },
      set(value) {
        this.workBreakPolicy.restriction_type = value;
        this.workBreakPolicy.block_duration = null;
        this.workBreakPolicy.earn_req_per_session = null;
        this.workBreakPolicy.earn_req_per_block = null;
      },
    },
    minimum: {
      get() {
        return this.workBreakPolicy.earn_req_per_session !== null;
      },
      set(value) {
        this.workBreakPolicy.earn_req_per_session = value ? '' : null;
      },
    },
    title() {
      return this.editing ? 'Edit work break policy' : 'Add work break policy';
    },
    ruleLabel() {
      return this.hasWithinRestriction ? 'Must work a minimum of' : 'After working at least';
    },
    rulePlaceholder() {
      return this.restriction === 1 ? '2' : '4';
    },
    hasRestriction() {
      return this.restriction > 0;
    },
    hasRepeatedRestriction() {
      return this.restriction > 1;
    },
    hasWithinRestriction() {
      return this.restriction === 2;
    },
    blockDuration: {
      get() {
        return this.workBreakPolicy.block_duration;
      },
      set(value) {
        this.workBreakPolicy.block_duration = value;
        if (this.restriction === 3) {
          this.workBreakPolicy.earn_req_per_block = value;
        }
      },
    },
    requireMinimum() {
      if (!this.hasRestriction) {
        return false;
      }

      return this.minimum;
    },
    maxBlockDuration() {
      // Organization can change max_block_duration settingApply.
      // We apply validation only for new records to avoid errors.
      return this.editing ? this.workBreakPolicy.block_duration : this.workBreaksSettings.max_block_duration
    },
    noEditTooltip() {
      return 'This cannot be edited once the policy has been created.';
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    paidOptions() {
      return [
        { id: 1, text: 'Paid' },
        { id: 0, text: 'Unpaid' },
      ];
    },
    save() {
      this.$refs.dialogObserver.validate().then((result) => {
        if (result) {
          this.sendingRequest = true;

          if (this.editing) {
            this.update();
          } else {
            this.create();
          }
        }
      });
    },
    requestParams() {
      return {
        work_break_policy: this.workBreakPolicy,
      };
    },
    membersOptions() {
      return this.availableMembers.map((member) => ({ id: member.id, text: member.name }));
    },
    showMessage(message) {
      this.$appMessage(message);
    },
    update() {
      this.$store
        .dispatch('updateWorkBreakPolicy', { id: this.workBreakPolicy.id, params: this.requestParams() })
        .then(this.doneRequest, this.failedRequest)
        .then(this.finishRequest);
    },
    create() {
      this.$store
        .dispatch('createWorkBreakPolicy', this.requestParams())
        .then(this.doneRequest, this.failedRequest)
        .then(this.finishRequest);
    },
    doneRequest(response) {
      if (response.success) {
        if (response.save_members_async) {
          this.showMessage(`
            Work break policy is being ${this.editing ? 'updated' : 'created'}.
            This can take a couple of minutes. You will receive an email when this is complete.
          `);
        } else {
          if (this.editing) {
            this.showMessage('Work break policy updated');
          } else {
            this.showMessage('Work break policy created');
          }
        }
        this.closeDialog();
      } else {
        if (response.errors) {
          this.$refs.dialogObserver.setErrors(response.errors);
        } else if (response.alert) {
          this.showMessage(response.alert);
        } else {
          this.showMessage('Unexpected error. Please try again.');
        }
      }
    },
    failedRequest(_error) {
      this.showMessage('Unexpected error. Please try again.');
    },
    finishRequest() {
      this.sendingRequest = false;
    },
  },
};
/* eslint-enable vue/no-mutating-props */
</script>
